import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { esignatureService } from "../../services/esignature";
import "./DocumentStatusModal.css";

function DocumentStatusModal({ isOpen, onClose, recruit }) {
  const { getVerifiedJWT } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && recruit) {
      loadDocumentForRecruit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recruit]);

  const loadDocumentForRecruit = async () => {
    setLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Get all documents
      const documents = await esignatureService.getDocuments(jwt);

      // Find document that has this recruit as a recipient
      const recruitDocument = documents.find(doc =>
        doc.recipients?.some(
          r =>
            r.full_name.toLowerCase().includes(recruit.name.toLowerCase()) ||
            r.email.toLowerCase() === recruit.email?.toLowerCase()
        )
      );

      if (recruitDocument) {
        // Get full document details with recipients
        const fullDoc = await esignatureService.getDocument(
          recruitDocument.id,
          jwt
        );
        setDocument(fullDoc);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = status => {
    switch (status?.toLowerCase()) {
      case "signed":
        return "status-signed";
      case "viewed":
        return "status-viewed";
      case "sent":
        return "status-sent";
      case "pending":
        return "status-pending";
      default:
        return "status-unknown";
    }
  };

  const getStatusLabel = status => {
    switch (status?.toLowerCase()) {
      case "signed":
        return "✓ Signed";
      case "viewed":
        return "👁 Viewed";
      case "sent":
        return "📧 Sent";
      case "pending":
        return "⏳ Pending";
      default:
        return status || "Unknown";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="document-status-modal-overlay" onClick={onClose}>
      <div
        className="document-status-modal-content"
        onClick={e => e.stopPropagation()}
      >
        <div className="document-status-modal-header">
          <h2>Document Status</h2>
          <button className="document-status-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="document-status-modal-body">
          {loading ? (
            <div className="loading-state">
              <p>Loading document status...</p>
            </div>
          ) : !document ? (
            <div className="no-document-state">
              <p>No document found for {recruit?.name}</p>
            </div>
          ) : (
            <>
              <div className="document-info-section">
                <h3>{document.title}</h3>
                <p className="document-status">
                  Status:{" "}
                  <span
                    className={`badge ${getStatusBadgeClass(document.status)}`}
                  >
                    {document.status}
                  </span>
                </p>
                {document.sent_at && (
                  <p className="document-date">
                    Sent: {new Date(document.sent_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className="recipients-status-section">
                <h4>Signing Progress</h4>
                <div className="recipients-list">
                  {document.recipients
                    ?.sort(
                      (a, b) => (a.signing_order || 0) - (b.signing_order || 0)
                    )
                    .map(recipient => (
                      <div key={recipient.id} className="recipient-status-item">
                        <div className="recipient-status-header">
                          <div
                            className="signing-order-badge"
                            style={{
                              backgroundColor: recipient.color || "#4A90E2",
                            }}
                          >
                            {recipient.signing_order}
                          </div>
                          <div className="recipient-status-info">
                            <div className="recipient-status-name">
                              {recipient.full_name}
                            </div>
                            <div className="recipient-status-email">
                              {recipient.email}
                            </div>
                          </div>
                          <div
                            className={`status-badge ${getStatusBadgeClass(recipient.status)}`}
                          >
                            {getStatusLabel(recipient.status)}
                          </div>
                        </div>
                        {recipient.signed_at && (
                          <div className="recipient-status-date">
                            Signed:{" "}
                            {new Date(recipient.signed_at).toLocaleString()}
                          </div>
                        )}
                        {recipient.viewed_at && !recipient.signed_at && (
                          <div className="recipient-status-date">
                            Viewed:{" "}
                            {new Date(recipient.viewed_at).toLocaleString()}
                          </div>
                        )}
                        {recipient.sent_at &&
                          !recipient.viewed_at &&
                          !recipient.signed_at && (
                            <div className="recipient-status-date">
                              Sent:{" "}
                              {new Date(recipient.sent_at).toLocaleString()}
                            </div>
                          )}
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>

        {document && (
          <div className="document-status-modal-footer">
            <button className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentStatusModal;
